import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const TenantContext = createContext({
  tenant: null,
  detected: false,
  loading: true,
  error: null,
  refresh: () => {},
});

export const useTenant = () => useContext(TenantContext);

/**
 * Resolve the current tenant from either:
 *  - a subdomain (e.g. fxdrip.astraterminal.net)  →  ?host=fxdrip.astraterminal.net
 *  - a custom domain (e.g. fxdrip.com)             →  ?host=fxdrip.com
 *  - a path slug (e.g. astraterminal.net/b/fxdrip) → ?slug=fxdrip
 *
 * If none resolves to a broker, tenant is null → the root marketing site renders.
 */

// Hostnames that are DEFINITELY the platform itself — never a tenant.
const PLATFORM_HOSTS = new Set([
  'astraterminal.net',
  'www.astraterminal.net',
  'astraterminal.com',
  'www.astraterminal.com',
  'localhost',
  '127.0.0.1',
]);

function detectTenant() {
  if (typeof window === 'undefined') return { slug: null, host: null };
  const host = window.location.hostname;
  const path = window.location.pathname;

  // Path-mode: /b/<slug>/*
  const pathMatch = path.match(/^\/b\/([a-z0-9][a-z0-9-]{0,40})(\/|$)/i);
  if (pathMatch) {
    const slug = pathMatch[1].toLowerCase();
    try { sessionStorage.setItem('astra.tenant_slug', slug); } catch { /* ignore */ }
    return { slug, host: null };
  }

  // Subdomain-mode: <sub>.astraterminal.net (skip www + root)
  const parts = host.split('.');
  const isAstra = host.endsWith('astraterminal.net') || host.endsWith('astraterminal.com');
  if (isAstra && parts.length >= 3 && parts[0] !== 'www' && parts[0] !== 'astraterminal') {
    return { slug: null, host };
  }

  // Custom-domain mode: any hostname that isn't the platform itself and isn't
  // an internal preview URL is a potential broker custom domain (e.g. fxdrip.com).
  // Let the backend confirm/deny — if it returns 404 we fall back to marketing.
  if (
    !PLATFORM_HOSTS.has(host) &&
    !host.endsWith('.preview.emergentagent.com') &&
    !host.endsWith('.emergentagent.com') &&
    !host.endsWith('.netlify.app') &&
    // Don't try to resolve bare IPs
    !/^(\d{1,3}\.){3}\d{1,3}$/.test(host) &&
    host.includes('.')
  ) {
    return { slug: null, host };
  }

  // Fallback for path-mode: if we previously saw /b/<slug> in this session AND
  // the user is currently on a tenant-scoped surface (client/broker portal OR the
  // white-labelled trading terminal), continue treating this tab as tenant-scoped
  // so in-SPA navigation preserves the broker's branding. We intentionally do NOT
  // hijack the root marketing site, /features, /legal, /admin, etc.
  if (path.startsWith('/platform/') || path === '/trading' || path.startsWith('/trading/') ||
      path === '/astratradingterminal' || path.startsWith('/astratradingterminal/') ||
      path.startsWith('/legal/')) {
    try {
      const remembered = sessionStorage.getItem('astra.tenant_slug');
      if (remembered) return { slug: remembered, host: null };
    } catch { /* ignore */ }
  }

  return { slug: null, host: null };
}

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(null);
  const [detected, setDetected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTenant = useCallback(async () => {
    const { slug, host } = detectTenant();
    const hasContext = !!(slug || host);
    setDetected(hasContext);
    if (!hasContext) {
      setTenant(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const qs = slug ? `slug=${encodeURIComponent(slug)}` : `host=${encodeURIComponent(host)}`;
      const res = await fetch(`${API}/api/admin/tenant/resolve?${qs}`);
      if (!res.ok) {
        setTenant(null);
        setError(res.status === 404 ? 'not_found' : 'error');
        return;
      }
      const data = await res.json();
      // Editor draft preview mode: /b/<slug>?editor=1&draft=1 swaps published blocks
      // with draft_blocks so admins can preview unsaved edits without publishing.
      try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('editor') === '1' && params.get('draft') === '1' && data?.landing?.draft_blocks) {
          data.landing = { ...data.landing, blocks: data.landing.draft_blocks };
        }
      } catch { /* ignore */ }
      setTenant(data);
    } catch (e) {
      setError('network');
      setTenant(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTenant(); }, [fetchTenant]);

  // Apply broker branding as CSS vars whenever tenant changes (used by landing page + downstream)
  useEffect(() => {
    const root = document.documentElement;
    if (tenant?.branding) {
      const b = tenant.branding;
      if (b.primary_color) root.style.setProperty('--broker-primary', b.primary_color);
      if (b.accent_color)  root.style.setProperty('--broker-accent', b.accent_color);
      if (b.background_color) root.style.setProperty('--broker-bg', b.background_color);
      if (b.text_color)    root.style.setProperty('--broker-text', b.text_color);

      // Swap favicon so the browser tab shows the broker's icon instead of
      // AstraTerminal's default. Supports hosted URLs, same-origin paths
      // (/assets/foo.svg), and base64 data URLs.
      if (b.favicon_url) {
        try {
          const href = b.favicon_url;
          // Remove ALL existing icon links so the browser doesn't fall back
          // to the stale default. Covers <link rel="icon">, "shortcut icon",
          // and "apple-touch-icon".
          document.querySelectorAll(
            "link[rel='icon'], link[rel~='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']"
          ).forEach(el => el.parentNode && el.parentNode.removeChild(el));

          const guessType = (u) => {
            const s = String(u).toLowerCase();
            if (s.startsWith('data:')) {
              const m = s.match(/^data:([^;,]+)/);
              return m ? m[1] : 'image/png';
            }
            if (s.endsWith('.svg')) return 'image/svg+xml';
            if (s.endsWith('.ico')) return 'image/x-icon';
            if (s.endsWith('.png')) return 'image/png';
            if (s.endsWith('.jpg') || s.endsWith('.jpeg')) return 'image/jpeg';
            return 'image/png';
          };

          const link = document.createElement('link');
          link.rel = 'icon';
          link.type = guessType(href);
          link.href = href;
          document.head.appendChild(link);

          const apple = document.createElement('link');
          apple.rel = 'apple-touch-icon';
          apple.href = href;
          document.head.appendChild(apple);
        } catch { /* ignore favicon errors */ }
      }
    } else {
      root.style.removeProperty('--broker-primary');
      root.style.removeProperty('--broker-accent');
      root.style.removeProperty('--broker-bg');
      root.style.removeProperty('--broker-text');
    }
  }, [tenant]);

  return (
    <TenantContext.Provider value={{ tenant, detected, loading, error, refresh: fetchTenant }}>
      {children}
    </TenantContext.Provider>
  );
}
